import { useState, useRef, useEffect } from 'react';
import { getWithExpiry, setWithExpiry } from '../utils/sessionStorage';
import { S3UploadMetadata } from '../types/file';
import { isUserFirefox } from '../utils/firefoxUtils';
import { GENERIC_ERROR_MESSAGES } from '../constants/permissionMessages';
import {
  logScreenerRecordingStarted,
  logScreenerRecordingCompleted,
  logScreenerCameraError,
  logScreenerUploadError,
} from '../utils/statsig';
import { getDeviceType } from '../utils/getDeviceType';
import { getBrowser } from '../utils/getBrowser';
import { getMediaDuration } from '../utils/getMediaDuration';
import { TakeFailure } from '../types/interview';

// VI-431: how long to wait for the streaming socket to open before failing the attempt.
const WS_CONNECT_TIMEOUT_MS = 10000;
// VI-431: thumbnail generation must never block the take (and thus Submit) forever.
const THUMBNAIL_TIMEOUT_MS = 5000;

// VI-431: start failures the candidate can act on (camera/mic busy or missing).
const isDeviceUnavailableError = (error: unknown) =>
  ['NotReadableError', 'NotFoundError', 'TrackStartError', 'DevicesNotFoundError'].includes(
    (error as { name?: string } | null)?.name ?? '',
  );

/*
  This hook is used to record audio and video from a media stream.
  per checking Safari, use mp4 format. - https://stackoverflow.com/a/73480671
  Firefox, use webm format.
  Other browsers, use webm or vp9 format.
*/
export const useRecording = () => {
  const fallBackGuid = getWithExpiry('applicantGuid');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const chunksRef = useRef<BlobPart[]>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcription, setTranscription] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [deviceRemovedDuringRecording, setDeviceRemovedDuringRecording] = useState(false);
  // VI-431: set when the current recording attempt fails; cleared on the next attempt.
  const [takeFailure, setTakeFailure] = useState<TakeFailure | null>(null);
  // VI-431: true from recorder start until the take is delivered (or discarded)
  // by recorder.onstop — Submit must wait so it never pairs a stale take.
  const [isFinalizingTake, setIsFinalizingTake] = useState(false);
  // VI-431: bumped on every new or cancelled attempt so a superseded attempt's
  // late socket/timeout events can't fail (or hijack) the current one.
  const attemptIdRef = useRef(0);

  // for audio level checks
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioRecordingError, setAudioRecordingError] = useState<string | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const AUDIO_LEVEL_THRESHOLD = 1; // strict
  const NO_AUDIO_THRESHOLD = 10; // seconds

  // audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const recordingStartTimeRef = useRef<number | null>(null);
  const currentQuestionIndexRef = useRef<number>(0);
  // VI-368: stores the duration of the last completed recording so recorder.onstop
  // can include it in the onTakeReady payload (recordingStartTimeRef is cleared in
  // stopStreamedRecording before onstop fires).
  const lastRecordingDurationRef = useRef<number>(0);

  // VI-368: pre-warmed recording session — set up during countdown so that
  // startPreparedRecording() can call recorder.start() with zero latency.
  type PreparedSession = {
    recorder: MediaRecorder;
    ws: WebSocket;
    stream: MediaStream;
    stopTracks: () => void; // stops the recording AND original camera/mic tracks
    questionIndex: number;
    applicantGuid: string;
  };
  const preparedSessionRef = useRef<PreparedSession | null>(null);

  const checkAudioLevel = (stream: MediaStream) => {
    setAudioRecordingError(null);
  
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    if (!analyserRef.current) {
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
    }
    const source = audioContextRef.current.createMediaStreamSource(stream);
    source.connect(analyserRef.current);
  
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
  
    const checkLevel = () => {
      if (!analyserRef.current) return;
  
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      setAudioLevel(average);
  
      if (average >= AUDIO_LEVEL_THRESHOLD) {
        // Audio detected: clear silence timer if running
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
        setAudioRecordingError(null);
      } else {
        // No audio detected: start/restart silence timer
        if (!silenceTimeoutRef.current) {
          silenceTimeoutRef.current = setTimeout(() => {
            setAudioRecordingError("We can't hear you, please double check your audio devices.");
          }, NO_AUDIO_THRESHOLD * 1000);
        }
      }
  
      animationFrameRef.current = requestAnimationFrame(checkLevel);
    };
  
    checkLevel();
  };

  const stopAudioMonitoring = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  
    // Clear the silence timer
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  };

  // COMMENTED OUT - Device change monitoring now handled by track 'ended' events
  // This prevents unnecessary checks when we're using browser default devices
  // useEffect(() => {
  //   console.warn("Effect: handleAudioDeviceChange, checking for device changes...")
  //   const handleAudioDeviceChange = async () => {
  //     setRecordingError(null);
  //     const devices = await navigator.mediaDevices.enumerateDevices();
  //     const audioInputs = devices.filter(d => d.kind === 'audioinput');
  //     const selectedAudioDeviceId = getWithExpiry('selectedAudioDeviceId');
  //     const selectedAudioExists = audioInputs.some(d => d.deviceId === selectedAudioDeviceId);
  // 
  //     if (!selectedAudioExists) {
  //       // Audio device was removed
  //       console.error("Audio device was removed")
  //       setDeviceRemovedDuringRecording(true);
  //       setRecordingError('Your microphone has been disconnected. Please reconnect your device and try again.');
  //       if (isRecording) {
  //         stopRecording();
  //       }
  //     } else {
  //       // Audio device is present (maybe reconnected)
  //       console.log("Audio device is present")
  //       setDeviceRemovedDuringRecording(false);
  //       setRecordingError(null);
  //       setError(null);
  //     }
  //   };
  // 
  //   navigator.mediaDevices.addEventListener('devicechange', handleAudioDeviceChange);
  //   return () => {
  //     navigator.mediaDevices.removeEventListener('devicechange', handleAudioDeviceChange);
  //   };
  // }, [isRecording, deviceRemovedDuringRecording]);

  // COMMENTED OUT - No longer checking device access on mount
  // Device access will be requested when recording actually starts
  // useEffect(() => {
  //   const checkInitialDeviceAccess = async () => {
  //     const videoDeviceId = getWithExpiry('selectedCameraDeviceId');
  //     const audioDeviceId = getWithExpiry('selectedAudioDeviceId');
  //     const constraints: MediaStreamConstraints = {
  //       video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
  //       audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
  //     };
  // 
  //     try {
  //       await navigator.mediaDevices.getUserMedia(constraints);
  //       setError(null);
  //     } catch (error: any) {
  //       await handleMediaError(error, constraints);
  //     }
  //   };
  // 
  //   if(!isUserFirefox()){
  //     checkInitialDeviceAccess();
  //   }
  //   // eslint-disable-next-line
  // }, []);

  const clearRecordingError = () => {
    setError(null);
    setRecordingError(null);
    setAudioRecordingError(null);
  };

  // Returns true when a specific, actionable message was set (VI-431: callers
  // fall back to the generic "Recording failed" message otherwise).
  const handleMediaError = async (error: any, constraints: MediaStreamConstraints): Promise<boolean> => {
    logScreenerCameraError({
      jobId: getWithExpiry('jobId') || '',
      companyId: getWithExpiry('companyId') || '',
      applicantGuid: getWithExpiry('applicantGuid') || undefined,
      deviceType: getDeviceType(),
      browser: getBrowser(),
      errorCode: error?.name,
      errorMessage: error?.message || String(error),
    });

    const isOverConstrainedException =
      error && typeof error === 'object' && 'name' in error && error.name === 'OverconstrainedError';
    const isNotAllowedException =
      error && typeof error === 'object' && 'name' in error && error.name === 'NotAllowedError';

    // if (isOverConstrainedException) {
    //   setError(`Your selected device is not available. Please try to reconnect your device and try again.`);
    //   console.error("Your selected device is not available. Please try to reconnect your device and try again.");
    // } 
    // if (isNotAllowedException || isOverConstrainedException) {
    console.error("ERROR: ", error)
      // browser permission denied
      let cameraDenied = false;
      let micDenied = false;

      //overconstrained
      let cameraConstrained = false;
      let micConstrained = false;
      try {
        await navigator.mediaDevices.getUserMedia({ video: constraints.video });
      } catch (e: any) {
        console.log("video error ", e);
        if (e && e.name === 'NotAllowedError') cameraDenied = true;
        if(e && e.name === 'OverconstrainedError') cameraConstrained = true;
      }
      try {
        await navigator.mediaDevices.getUserMedia({ audio: constraints.audio });
      } catch (e: any) {
        console.log("mic error ", e);
        if (e && e.name === 'NotAllowedError') micDenied = true;
        if(e && e.name === 'OverconstrainedError') micConstrained = true;
      }

      if (cameraDenied && micDenied) {
        setError("Can't access your camera and microphone. Please check your browser permissions and try again.");
        console.error("Can't access your camera and microphone. Please check your browser permissions and try again.");
      } else if (cameraDenied) {
        setError("Can't access your camera. Please check your browser permissions and try again.");
        console.error("Can't access your camera. Please check your browser permissions and try again.");
      } else if (micDenied) {
        setError("Can't access your microphone. Please check your browser permissions and try again.");
        console.error("Can't access your microphone. Please check your browser permissions and try again.");
      } else if (cameraConstrained && micConstrained) {
        setError("Your selected device for camera and microphone is not available. Please try to reconnect your device and try again.");
        console.error("Your selected device for camera and microphone is not available. Please try to reconnect your device and try again.");
      } else if (cameraConstrained) {
        setError("Your selected device for camera is not available. Please try to reconnect your device and try again.");
        console.error("Your selected device for camera is not available. Please try to reconnect your device and try again. (cameraConstrained)");
      } else if (micConstrained) {
        setError("Your selected device for microphone is not available. Please try to reconnect your device and try again.");
        console.error("Your selected device for microphone is not available. Please try to reconnect your device and try again. (micConstrained)");
      }
      console.log("handleMediaError called 2")
      console.log("cameraDenied", cameraDenied)
      console.log("micDenied", micDenied)
      console.log("cameraConstrained", cameraConstrained)
      console.log("micConstrained", micConstrained)
      return cameraDenied || micDenied || cameraConstrained || micConstrained;

    // } else {
    //   setError(`Recording failed. Please try again`);
    //   console.error("Recording failed. Please try again", error);
    // }
};

  const getVideoRecorderOptions = () => {
    const isFirefox = navigator.userAgent.indexOf("Firefox") !== -1;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                    /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Safari: prefer mp4
    if (isSafari) {
      if (MediaRecorder.isTypeSupported('video/mp4')) {
        return { mimeType: 'video/mp4' };
      }
      return {}; // Default
    }
    
    // Firefox: prefer webm
    if (isFirefox) {
      if (MediaRecorder.isTypeSupported('video/webm')) {
        return { mimeType: 'video/webm' };
      }
    }
    
    // Other browsers: try various formats
    const formats = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4'
    ];
    
    for (const format of formats) {
      if (MediaRecorder.isTypeSupported(format)) {
        return { mimeType: format };
      }
    }
    
    return {}; // Default fallback
  };

  // logic for appending <canvas> to <video>
  const createCompositeStream = async (
    canvasElement: HTMLCanvasElement | null,
    originalStream: MediaStream,
    isBlurEnabled: boolean
  ): Promise<MediaStream> => {
    
    if (isBlurEnabled && canvasElement) {
      console.log('🎯 Creating composite stream with canvas (blur enabled)');

      // Sanity check: caller (handleRecordButtonClick) is responsible for
      // ensuring the blur canvas has stable dims + a rendered frame before
      // we get here. If the canvas pixel buffer is still 0/default, fall
      // back to the original stream rather than encoding a track that will
      // resize mid-recording (was producing the "picture grows" S3 bug).
      if (canvasElement.width <= 0 || canvasElement.height <= 0) {
        console.warn(
          `⚠️ Canvas not sized (${canvasElement.width}x${canvasElement.height}); falling back to original stream`
        );
        return originalStream;
      }

      const canvasStream = canvasElement.captureStream(30);
      const videoTrack = canvasStream.getVideoTracks()[0];

      if (!videoTrack) {
        console.warn('⚠️ No video track from canvas, falling back to original stream');
        return originalStream;
      }
      const trackSettings = videoTrack.getSettings();
      console.log(
        `📐 Canvas track resolution: ${trackSettings.width}x${trackSettings.height} @ ${trackSettings.frameRate ?? 30}fps`
      );
      
      // Get audio tracks from original stream
      const audioTracks = originalStream.getAudioTracks();
      
      // Create new composite stream
      const compositeStream = new MediaStream();
      
      // Add canvas video track
      compositeStream.addTrack(videoTrack);
      
      // Add original audio tracks
      audioTracks.forEach(track => {
        compositeStream.addTrack(track);
      });
      
      console.log('✅ Composite stream created with canvas video + original audio');
      return compositeStream;
    } else {
      console.log('🎯 Using original stream (blur disabled)');
      return originalStream;
    }
  };

  // VI-368: payload delivered to Interview.tsx after each take stops (Test group only)
  type TakeReadyPayload = {
    ws: WebSocket;
    blob: Blob;
    url: string;
    thumbnail: string;
    duration: number;
  };

  // VI-431: the streaming socket of the most recent recording (legacy single-take
  // submit uses it with the hook's recordedBlob).
  const getActiveSocket = () => wsRef.current;
  const clearTakeFailure = () => setTakeFailure(null);

  // VI-368: Cancel any pre-warmed session that was not yet started.
  // Called when countdown is cancelled or an error occurs during prep.
  const cancelPreparedSession = () => {
    // VI-431: invalidate any attempt still connecting (not yet in preparedSessionRef).
    // Never the one already recording (e.g. a Stop click landing in the same frame
    // the countdown handed over to the recorder) — its failures must still surface.
    if (mediaRecorderRef.current?.state !== 'recording') attemptIdRef.current++;
    const session = preparedSessionRef.current;
    if (!session) return;
    if (session.ws.readyState !== WebSocket.CLOSED && session.ws.readyState !== WebSocket.CLOSING) {
      session.ws.close();
    }
    session.stopTracks();
    preparedSessionRef.current = null;
    console.log('[Pre-warm] Cancelled prepared session');
  };

  // VI-368: Begin recording on a pre-warmed session (WebSocket already open).
  // Called at countdown=0 so recording starts with zero latency.
  const startPreparedRecording = () => {
    const session = preparedSessionRef.current;
    if (!session) {
      console.error('[Pre-warm] startPreparedRecording: no prepared session');
      return;
    }
    // Update hook refs so stopStreamedRecording/submitSpecificWs work correctly
    mediaRecorderRef.current = session.recorder;
    mediaStreamRef.current = session.stream;
    wsRef.current = session.ws;
    preparedSessionRef.current = null;

    session.recorder.start(1000);
    setIsRecording(true);
    setIsFinalizingTake(true);
    recordingStartTimeRef.current = Date.now();
    currentQuestionIndexRef.current = session.questionIndex;
    logScreenerRecordingStarted({
      jobId: getWithExpiry('jobId') || '',
      companyId: getWithExpiry('companyId') || '',
      applicantGuid: session.applicantGuid,
      deviceType: getDeviceType(),
      questionNumber: session.questionIndex + 1,
    });
    console.log('[Pre-warm] MediaRecorder started (from prepared session)');
  };

  // VI-368: Set up stream + WebSocket during countdown so the socket is open
  // and the recorder is ready by the time the countdown reaches 0.
  // Returns true when WS is open and ready; false on any error (error already surfaced).
  const prepareStreamedRecording = async (
    applicantGuid: string = fallBackGuid,
    questionIndex: number,
    questionText: string,
    isLastQuestion: boolean = false,
    totalQuestions: number,
    s3MetaData: S3UploadMetadata,
    canvasRef?: React.RefObject<HTMLCanvasElement>,
    isBlurEnabled?: boolean,
    onTakeReady?: (payload: TakeReadyPayload) => void,
  ): Promise<boolean> => {
    // Cancel any leftover session from a previous attempt
    cancelPreparedSession();
    const attemptId = ++attemptIdRef.current;
    const isCurrentAttempt = () => attemptIdRef.current === attemptId;
    try {
      setError(null);
      setDeviceRemovedDuringRecording(false);
      setTakeFailure(null);
      setIsFinalizingTake(false);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
      setRecordingError(null);
      setProgress(0);

      const videoDeviceId = getWithExpiry('selectedCameraDeviceId') || undefined;
      const audioDeviceId = getWithExpiry('selectedAudioDeviceId') || undefined;
      const isAndroid = /android/i.test(navigator.userAgent);

      const constraints: MediaStreamConstraints = {
        video: isAndroid
          ? { facingMode: 'user', width: { exact: 640 }, height: { exact: 480 }, frameRate: { ideal: 30 } }
          : videoDeviceId
            ? { deviceId: { exact: videoDeviceId }, width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 } }
            : { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 } },
        audio: audioDeviceId
          ? { deviceId: { exact: audioDeviceId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
          : { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      };

      console.log('[Pre-warm] Getting media stream...');
      const originalStream = await navigator.mediaDevices.getUserMedia(constraints);
      if (!isCurrentAttempt()) {
        // Superseded while waiting for the camera (countdown cancelled / blur re-prepare).
        originalStream.getTracks().forEach(t => t.stop());
        return false;
      }

      const videoTrack = originalStream.getVideoTracks()[0];
      const audioTrack = originalStream.getAudioTracks()[0];
      if (videoTrack?.getSettings().deviceId) {
        setWithExpiry('selectedCameraDeviceId', videoTrack.getSettings().deviceId!, 24 * 60 * 60 * 1000);
      }
      if (audioTrack?.getSettings().deviceId) {
        setWithExpiry('selectedAudioDeviceId', audioTrack.getSettings().deviceId!, 24 * 60 * 60 * 1000);
      }

      const recordingStream = await createCompositeStream(canvasRef?.current || null, originalStream, isBlurEnabled || false);
      await checkAudioLevel(recordingStream);

      // VI-431: with blur on, the recorded video is the canvas track, but the real
      // camera track lives on originalStream — stop (and watch) both.
      const sessionTracks = Array.from(new Set([...recordingStream.getTracks(), ...originalStream.getTracks()]));
      const stopSessionTracks = () => sessionTracks.forEach(t => t.stop());

      // VI-431: per-attempt failure tracking. Any failure while this attempt is
      // live (connecting, prepared during the countdown, or recording) fails the
      // take: the candidate is told, the take is never kept or submittable, and it
      // doesn't consume a try. `let`s so handlers that fire before the socket or
      // recorder exist are safe.
      let sessionWs: WebSocket | null = null;
      let sessionRecorder: MediaRecorder | null = null;
      let wsSettled = false;
      let sessionFailed = false;
      let failureSurfaced = false;
      let recorderFinished = false;

      // Live = still connecting, prepared (countdown) or recording. Once the
      // recorder has stopped normally the take is complete locally, so a later
      // socket drop is recovered on Submit (HTTP fallback) and isn't a failure.
      // Sockets we close on purpose (cancelled countdown, non-selected takes) are
      // no longer live by the time their close event fires.
      const isSessionLive = () =>
        !wsSettled ||
        sessionRecorder?.state === 'recording' ||
        (!!sessionWs && preparedSessionRef.current?.ws === sessionWs);

      const surfaceFailure = (reason: TakeFailure['reason'], message: string) => {
        if (failureSurfaced || !isCurrentAttempt()) return;
        failureSurfaced = true;
        console.error(`[VI-431] Recording attempt failed (${reason}): ${message}`);
        setTakeFailure({ reason, message, at: Date.now() });
        stopAudioMonitoring();
        if (sessionWs && preparedSessionRef.current?.ws === sessionWs) {
          // Failed during the countdown — drop the prepared session.
          preparedSessionRef.current = null;
        }
        if (sessionRecorder?.state === 'recording') {
          sessionRecorder.stop(); // onstop sees sessionFailed and discards the take
        }
        stopSessionTracks();
        if (sessionWs && (sessionWs.readyState === WebSocket.OPEN || sessionWs.readyState === WebSocket.CONNECTING)) {
          sessionWs.close();
        }
        setIsRecording(false);
      };

      const failTake = (reason: TakeFailure['reason'], message: string) => {
        if (sessionFailed || !isSessionLive()) return;
        sessionFailed = true;
        surfaceFailure(reason, message);
      };

      // Monitor for device removal during recording (incl. the real camera track
      // behind the blur canvas, so unplugging it isn't recorded as a frozen frame)
      const videoTracks = Array.from(new Set([...recordingStream.getVideoTracks(), ...originalStream.getVideoTracks()]));
      const audioTracks = recordingStream.getAudioTracks();
      let hasHandledDisconnection = false;

      const handleTrackEnded = async (event: Event) => {
        const track = event.target as MediaStreamTrack;
        if (track.readyState === 'ended' && !hasHandledDisconnection) {
          hasHandledDisconnection = true;
          if (sessionFailed || !isSessionLive()) return;
          // Mark failed synchronously so a recorder auto-stop (all tracks ended)
          // can't deliver this partial take as a good one while we look up devices.
          sessionFailed = true;
          const storedCameraId = getWithExpiry('selectedCameraDeviceId');
          const storedMicId = getWithExpiry('selectedAudioDeviceId');
          const allDevices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = allDevices.filter(d => d.kind === 'videoinput');
          const audioDevices = allDevices.filter(d => d.kind === 'audioinput' && d.deviceId !== 'default' && d.deviceId !== 'communications');
          const cameraStillExists = !storedCameraId || videoDevices.some(d => d.deviceId === storedCameraId);
          const micStillExists = !storedMicId || audioDevices.some(d => d.deviceId === storedMicId);
          let errorMessage: string;
          if (!cameraStillExists && !micStillExists) {
            errorMessage = 'Your camera and microphone have been disconnected. Please reconnect and try again.';
          } else if (!cameraStillExists) {
            errorMessage = 'Your camera has been disconnected. Please reconnect and try again.';
          } else if (!micStillExists) {
            errorMessage = 'Your microphone has been disconnected. Please reconnect and try again.';
          } else {
            const device = track.kind === 'video' ? 'Camera' : 'Microphone';
            errorMessage = `${device} access was lost during recording. Please check your permissions and try again.`;
          }
          // Actionable failure → specific guidance (AC5).
          surfaceFailure('device', errorMessage);
        }
      };

      videoTracks.forEach(track => track.addEventListener('ended', handleTrackEnded));
      audioTracks.forEach(track => track.addEventListener('ended', handleTrackEnded));

      const options = await getVideoRecorderOptions();
      const recorder = new MediaRecorder(recordingStream, options);
      sessionRecorder = recorder;
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
          if (ws.readyState === WebSocket.OPEN) {
            console.log(`Sending chunk: ${Math.round(event.data.size / 1024)} KB`);
            ws.send(event.data);
          }
        }
      };

      recorder.onstop = async () => {
        console.log('[Pre-warm] MediaRecorder stopped');
        recorderFinished = true;
        if (sessionFailed) {
          // VI-431: a failed attempt is never kept as a take or made submittable.
          setRecordedBlob(null);
          setRecordedUrl(null);
          stopSessionTracks();
          videoTracks.forEach(t => t.removeEventListener('ended', handleTrackEnded));
          audioTracks.forEach(t => t.removeEventListener('ended', handleTrackEnded));
          setIsRecording(false);
          setIsFinalizingTake(false);
          return;
        }
        const mimeType = options.mimeType || 'video/webm';
        const videoBlob = new Blob(chunks, { type: mimeType });
        const videoUrl = URL.createObjectURL(videoBlob);
        setRecordedBlob(videoBlob);
        setRecordedUrl(videoUrl);
        const thumbnail = await Promise.race([
          generateThumbnail(videoBlob, questionIndex),
          new Promise<string>(resolve => setTimeout(() => resolve(''), THUMBNAIL_TIMEOUT_MS)),
        ]);
        // VI-702: use the blob's true duration so the thumbnail badge matches the player.
        const trueDuration = await getMediaDuration(videoBlob, lastRecordingDurationRef.current);
        if (onTakeReady) {
          onTakeReady({ ws, blob: videoBlob, url: videoUrl, thumbnail, duration: trueDuration });
        }
        stopSessionTracks();
        videoTracks.forEach(t => t.removeEventListener('ended', handleTrackEnded));
        audioTracks.forEach(t => t.removeEventListener('ended', handleTrackEnded));
        setIsRecording(false);
        setIsFinalizingTake(false);
      };

      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        const recorderError = (event as unknown as { error?: { name?: string; message?: string } }).error;
        logScreenerCameraError({
          jobId: getWithExpiry('jobId') || '',
          companyId: getWithExpiry('companyId') || '',
          applicantGuid: getWithExpiry('applicantGuid') || undefined,
          deviceType: getDeviceType(),
          browser: getBrowser(),
          errorCode: recorderError?.name || 'MediaRecorderError',
          errorMessage: recorderError?.message || 'MediaRecorder failed during recording',
        });
        // The recorder may already be inactive when 'error' fires, so don't gate
        // on liveness — only on the attempt not having finished cleanly.
        if (sessionFailed || recorderFinished) return;
        sessionFailed = true;
        surfaceFailure('recorder', GENERIC_ERROR_MESSAGES.recording_failed.message);
      };

      // Open WebSocket and resolve when it's ready
      const socketUrl = getWebSocketUrl();
      console.log('[Pre-warm] Connecting WebSocket:', socketUrl);
      const ws = new WebSocket(socketUrl);
      sessionWs = ws;

      const logSocketError = (errorCode: string, errorMessage: string) => {
        logScreenerUploadError({
          jobId: getWithExpiry('jobId') || '',
          companyId: getWithExpiry('companyId') || '',
          applicantGuid: getWithExpiry('applicantGuid') || undefined,
          deviceType: getDeviceType(),
          questionNumber: questionIndex + 1,
          errorCode,
          errorMessage,
        });
      };

      // Attached up front (not after stop) so a server error during the countdown
      // or recording is caught while the attempt is still live.
      ws.onmessage = (event) => {
        if (typeof event.data !== 'string') return;
        try {
          const parsedData = JSON.parse(event.data);
          if (parsedData.type === 'upload_complete') {
            setVideoUrl(parsedData.videoUrl);
            setAudioUrl(parsedData.audioUrl);
            setTranscription(parsedData.transcription);
            setProgress(100);
          } else if (parsedData.type === 'error') {
            // Server-side processing failed (e.g. FFmpeg couldn't read the live
            // stream). The local recording is still complete, so don't fail the
            // take: on Submit the server rejects process_recording and the take is
            // re-uploaded over HTTP instead.
            console.error('Server error:', parsedData.message);
            logSocketError(parsedData.code || 'ServerUploadError', parsedData.message || 'Server returned error during upload');
          } else if (parsedData.type === 'answer_submitted') {
            console.log('Answer submitted to API', parsedData.isReconciled);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      return new Promise<boolean>((resolve) => {
        const settle = (value: boolean) => {
          if (wsSettled) return;
          wsSettled = true;
          clearTimeout(connectTimer);
          resolve(value);
        };

        // VI-431: never leave the candidate stuck on the countdown if the socket
        // neither opens nor errors.
        const connectTimer = setTimeout(() => {
          if (wsSettled) return;
          console.error('[Pre-warm] WebSocket connect timed out');
          logSocketError('WebSocketTimeout', 'WebSocket did not open in time during pre-warm');
          failTake('timeout', GENERIC_ERROR_MESSAGES.recording_failed.message);
          ws.close();
          stopSessionTracks();
          settle(false);
        }, WS_CONNECT_TIMEOUT_MS);

        ws.onopen = () => {
          // Failed while connecting, or superseded (countdown cancelled / blur
          // re-prepare): release everything instead of becoming the prepared session.
          if (sessionFailed || !isCurrentAttempt()) {
            ws.close();
            stopSessionTracks();
            return;
          }
          console.log('[Pre-warm] WebSocket open — sending metadata');
          ws.send(JSON.stringify({
            type: 'metadata',
            applicantGuid,
            questionId: questionIndex,
            questionText,
            isLastQuestion,
            totalQuestions,
            s3MetaData,
          }));
          // Store the prepared session — startPreparedRecording() will call recorder.start()
          preparedSessionRef.current = { recorder, ws, stream: recordingStream, stopTracks: stopSessionTracks, questionIndex, applicantGuid };
          settle(true);
        };

        ws.onerror = () => {
          console.error('[Pre-warm] WebSocket error');
          logSocketError('WebSocketError', wsSettled ? 'WebSocket error during recording' : 'WebSocket connection error during pre-warm');
          failTake('connection', GENERIC_ERROR_MESSAGES.recording_failed.message);
          settle(false);
        };

        ws.onclose = () => {
          console.log('[Pre-warm] WebSocket closed');
          failTake('connection', GENERIC_ERROR_MESSAGES.recording_failed.message);
          if (!wsSettled) stopSessionTracks();
          settle(false);
        };
      });
    } catch (error: unknown) {
      // VI-431: a superseded attempt's start failure must not disturb the current one.
      if (!isCurrentAttempt()) return false;
      const isOverconstrained = error instanceof DOMException && error.name === 'OverconstrainedError';
      console.error('[Pre-warm] Setup failed:', error);
      if (isOverconstrained) {
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          const vt = fallbackStream.getVideoTracks()[0];
          const at = fallbackStream.getAudioTracks()[0];
          if (vt?.getSettings().deviceId) setWithExpiry('selectedCameraDeviceId', vt.getSettings().deviceId!, 24 * 60 * 60 * 1000);
          if (at?.getSettings().deviceId) setWithExpiry('selectedAudioDeviceId', at.getSettings().deviceId!, 24 * 60 * 60 * 1000);
          fallbackStream.getTracks().forEach(t => t.stop());
          return prepareStreamedRecording(applicantGuid, questionIndex, questionText, isLastQuestion, totalQuestions, s3MetaData, canvasRef, isBlurEnabled, onTakeReady);
        } catch {
          setError(GENERIC_ERROR_MESSAGES.device_constraint_error.message);
          return false;
        }
      }
      const handled = await handleMediaError(error, { video: true, audio: true });
      if (!handled) {
        // VI-431: never leave a start failure silent — specific guidance when the
        // camera/mic is busy or missing (AC5), generic otherwise.
        setTakeFailure({
          reason: 'start',
          message: isDeviceUnavailableError(error)
            ? GENERIC_ERROR_MESSAGES.device_constraint_error.message
            : GENERIC_ERROR_MESSAGES.recording_failed.message,
          at: Date.now(),
        });
      }
      return false;
    }
  };

  // for ws streaming — thin wrapper: prepare then start immediately (non-countdown path)
  const startStreamedRecording = async (
    applicantGuid: string = fallBackGuid,
    questionIndex: number,
    questionText: string,
    isLastQuestion: boolean = false,
    totalQuestions: number,
    s3MetaData: S3UploadMetadata,
    canvasRef?: React.RefObject<HTMLCanvasElement>,
    isBlurEnabled?: boolean,
    onTakeReady?: (payload: TakeReadyPayload) => void
  ) => {
    try {
      const ready = await prepareStreamedRecording(
        applicantGuid, questionIndex, questionText, isLastQuestion,
        totalQuestions, s3MetaData, canvasRef, isBlurEnabled, onTakeReady
      );
      if (!ready) return null;
      startPreparedRecording();
      return true;
    } catch (error: unknown) {
      console.error('startStreamedRecording failed:', error);
      await handleMediaError(error, { video: true, audio: true });
      return null;
    }
  };

  // for ws stop streaming
  const stopStreamedRecording = () => {
    stopAudioMonitoring();
    console.log('stopStreamedRecording: Stopping streamed recording');

    if (recordingStartTimeRef.current) {
      const recordingLengthSeconds = Math.round((Date.now() - recordingStartTimeRef.current) / 1000);
      lastRecordingDurationRef.current = recordingLengthSeconds;
      logScreenerRecordingCompleted({
        jobId: getWithExpiry('jobId') || '',
        companyId: getWithExpiry('companyId') || '',
        applicantGuid: getWithExpiry('applicantGuid') || undefined,
        deviceType: getDeviceType(),
        questionNumber: currentQuestionIndexRef.current + 1,
        recordingLengthSeconds,
      });
      recordingStartTimeRef.current = null;
    }

    // Stop MediaRecorder if it's recording
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    // Use wsRef to access the WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log("User has stopped recording");
    }
    setIsRecording(false);
  };

  const handleSubmitStreamRecording = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'process_recording' }));
      console.log('Sent process_recording signal to server');
    } else {
      console.error('Cannot submit recording: WebSocket is not connected');
    }
  };

  // VI-368: submit a specific WebSocket's recording (Test group — user selected a take).
  // Sends process_recording only to the chosen take; callers must close all other WSes.
  const submitSpecificWs = (ws: WebSocket) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'process_recording' }));
      console.log('Sent process_recording signal to selected take WebSocket');
    } else {
      console.error('Cannot submit selected take: WebSocket is not open');
    }
  };

  const getWebSocketUrl = () => {
    const isDev =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
    const isHttps = window.location.protocol === 'https:';

    // HTTPS dev: route through the Vite dev server's /stream proxy (ws:true in
    // vite.config.ts) so TLS terminates at :3000 and is forwarded as plain ws://
    // to the backend on :4001. Direct wss://localhost:4001 fails because the
    // backend WS server doesn't speak TLS in dev.
    if (isDev && isHttps) {
      return `wss://${window.location.host}/stream`;
    }

    if (isDev) {
      return `ws://${window.location.hostname}:4001/stream`;
    }

    return `${isHttps ? 'wss:' : 'ws:'}//${window.location.host}/stream`;
  };

  const generateThumbnail = async (videoBlob: Blob, questionIndex: number): Promise<string> => {
    const videoUrl = URL.createObjectURL(videoBlob);
    const video = document.createElement('video');
    video.src = videoUrl;
    video.muted = true;

    return new Promise<string>((resolve) => {
      video.onloadeddata = () => {
        video.currentTime = Math.min(1, video.duration / 2);
      };

      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
          const questionId = `question_${questionIndex}`;
          setThumbnails(prev => ({ ...prev, [questionId]: thumbnailUrl }));
          URL.revokeObjectURL(videoUrl);
          resolve(thumbnailUrl);
        } else {
          URL.revokeObjectURL(videoUrl);
          resolve('');
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(videoUrl);
        resolve('');
      };
    });
  };

  const stopRecording = (): void => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        console.log('Recording stopping...');
      } catch (error) {
        console.error('Error stopping recording:', error);
      }
    }
  };

  const clearRecording = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
  };

  const setRecordedData = (blob: Blob, url: string) => {
    setRecordedBlob(blob);
    setRecordedUrl(url);
  };

  return {
    isRecording,
    recordedBlob,
    recordedUrl,
    setIsRecording,
    stopRecording,
    clearRecording,
    setRecordedData,
    startStreamedRecording,
    prepareStreamedRecording,
    startPreparedRecording,
    cancelPreparedSession,
    stopStreamedRecording,
    submitSpecificWs,
    videoUrl,
    audioUrl,
    transcription,
    handleSubmitStreamRecording,
    error,
    deviceRemovedDuringRecording,
    setDeviceRemovedDuringRecording,
    setError,
    audioRecordingError,
    recordingError,
    clearRecordingError,
    takeFailure,
    clearTakeFailure,
    getActiveSocket,
    isFinalizingTake,
  };
};

export default useRecording;